import { Injectable, inject } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ErrorMessageService {
    private readonly translateService = inject(TranslateService);

    async resolveApiError(error: unknown, fallbackSpaKey: string = 'unknown'): Promise<string> {
        const apiTranslationKey = this.resolveApiTranslationKey(error);
        if (apiTranslationKey) {
            const translated = await this.translate(apiTranslationKey);
            if (translated !== apiTranslationKey) {
                return translated;
            }
        }

        return this.resolveSpaError(fallbackSpaKey);
    }

    async resolveSpaError(spaKey: string = 'unknown'): Promise<string> {
        const normalizedKey = this.normalizeSpaKey(spaKey);
        const translated = await this.translate(normalizedKey);

        if (translated !== normalizedKey || normalizedKey === 'err.spa.unknown') {
            return translated;
        }

        return this.translate('err.spa.unknown');
    }

    private resolveApiTranslationKey(error: unknown): string | null {
        const errorCode = this.extractApiErrorCode(error);
        return errorCode ? `err.api.${errorCode}` : null;
    }

    private extractApiErrorCode(error: unknown): string | null {
        const candidatePaths: readonly (readonly string[])[] = [
            ['body', 'code'],
            ['code'],
            ['error', 'body', 'code'],
            ['error', 'code'],
            ['body', 'client', 'code'],
            ['client', 'code'],
        ];

        for (const path of candidatePaths) {
            const value = this.readString(error, path);
            if (value && this.isSupportedApiErrorCode(value)) {
                return value;
            }
        }

        return null;
    }

    private normalizeSpaKey(spaKey: string): string {
        return spaKey.startsWith('err.spa.') ? spaKey : `err.spa.${spaKey}`;
    }

    private async translate(key: string): Promise<string> {
        return firstValueFrom(this.translateService.get(key));
    }

    private readString(source: unknown, path: readonly string[]): string | null {
        let current: unknown = source;

        for (const segment of path) {
            if (!this.isRecord(current) || !(segment in current)) {
                return null;
            }

            current = current[segment];
        }

        return typeof current === 'string' ? current : null;
    }

    private isSupportedApiErrorCode(value: string): boolean {
        return value.startsWith('err.auth.') || value.startsWith('err.identity.');
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }
}
